import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ConsultationForm from '../components/ConsultationForm';
import ImageViewer from '../components/ImageViewer';
import { portfolioData } from '../data/portfolioData';
import '../styles/Portfolio.css';

// Get unique categories from portfolio data and format them for display
const categories = ['All', ...new Set(portfolioData.map(item => item.category))];

// Format category for display (capitalize first letter)
const formatCategory = (category) => {
    if (!category) return '';
    return category.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

const Portfolio = () => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [filteredItems, setFilteredItems] = useState(portfolioData);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [isViewerOpen, setIsViewerOpen] = useState(false);

    // Handle body overflow when modal is open/closed
    useEffect(() => {
        if (showQuoteForm || selectedProject || isViewerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function to reset overflow when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showQuoteForm, selectedProject, isViewerOpen]);

    useEffect(() => {
        // Filter items when activeCategory changes
        const filtered = activeCategory === 'All'
            ? [...portfolioData]
            : portfolioData.filter(item => item.category === activeCategory);
        setFilteredItems(filtered);
    }, [activeCategory]);

    const openProjectDetails = (project) => {
        setSelectedProject(project);
        document.body.style.overflow = 'hidden';
    };

    const closeProjectDetails = () => {
        setSelectedProject(null);
        setSelectedImageIndex(0);
        setIsViewerOpen(false);
        document.body.style.overflow = 'unset';
    };

    const openImageViewer = (index, e) => {
        e.stopPropagation(); // Prevent triggering project details
        setSelectedImageIndex(index);
        setIsViewerOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeImageViewer = () => {
        setIsViewerOpen(false);
        document.body.style.overflow = 'unset';
    };

    const goToNextImage = () => {
        setSelectedImageIndex(prevIndex =>
            prevIndex === selectedProject.gallery.length - 1 ? 0 : prevIndex + 1
        );
    };

    const goToPrevImage = () => {
        setSelectedImageIndex(prevIndex =>
            prevIndex === 0 ? selectedProject.gallery.length - 1 : prevIndex - 1
        );
    };

    return (
        <div className="portfolio-container">
            {/* Header Section */}
            <div className="portfolio-header">
                <h2 className="section-title">
                    Our <span className="highlight">Portfolio</span>
                </h2>
                <p className="section-subtitle">
                    Explore our latest interior design projects and get inspired for your next project.
                </p>

                {/* Category Filters */}
                <div className="category-filters">
                    {categories.map(category => (
                        <button
                            key={category}
                            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category)}
                        >
                            {formatCategory(category)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Portfolio Grid */}
            <div className="portfolio-grid">
                {filteredItems.length > 0 ? (
                    filteredItems.map(project => (
                        <div
                            key={project.id}
                            className="portfolio-card"
                            onClick={() => openProjectDetails(project)}
                        >
                            <div className="card-image">
                                <img
                                    src={project.bannerImage}
                                    alt={project.title}
                                    className="project-image"
                                />

                            </div>
                            <div className="card-content">
                                <div className="card-header">
                                    <h3>{project.title}</h3>
                                    <div className="card-meta">
                                        <span>{project.client}</span>
                                        <span className="meta-separator">•</span>
                                        <span>{project.year}</span>
                                    </div>
                                </div>
                                <p className="card-desc">
                                    {project.description.length > 100
                                        ? `${project.description.substring(0, 100)}...`
                                        : project.description}
                                </p>
                                <div className="card-footer">
                                    <button className="view-btn">View Project</button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-projects">
                        <p>No projects found in this category.</p>
                    </div>
                )}
            </div>

            {/* Project Details Modal */}
            {selectedProject && (
                <div className="project-modal">
                    <div className="modal-overlay" onClick={closeProjectDetails}></div>
                    <div className="modal-content">
                        <button className="close-modal" onClick={closeProjectDetails}>&times;</button>

                        <div className="project-gallery">
                            {selectedProject.gallery.map((image, index) => (
                                <div
                                    key={index}
                                    className="gallery-item"
                                    onClick={(e) => openImageViewer(index, e)}
                                >
                                    <img
                                        src={image}
                                        alt={`${selectedProject.title} - ${index + 1}`}
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>

                        {isViewerOpen && selectedProject && (
                            <ImageViewer
                                images={selectedProject.gallery}
                                currentIndex={selectedImageIndex}
                                onClose={closeImageViewer}
                                onNext={goToNextImage}
                                onPrev={goToPrevImage}
                            />
                        )}

                        <div className="project-details">
                            <h2>{selectedProject.title}</h2>
                            {/* <div className="project-meta">
                                <span><strong>Client:</strong> {selectedProject.client}</span>
                                <span><strong>Year:</strong> {selectedProject.year}</span>
                                <span><strong>Category:</strong> {selectedProject.category}</span>
                            </div> */}

                            <div className="project-description">
                                <h3>Project Overview</h3>
                                <p>{selectedProject.description}</p>
                            </div>

                            <div className="project-features">
                                <h3>Key Features</h3>
                                <ul>
                                    {selectedProject.features.map((feature, index) => (
                                        <li key={index}>
                                            <span className="feature-icon">{selectedProject.icon}</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="project-cta">
                                <button
                                    className="cta-button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowQuoteForm(true);
                                        setSelectedProject(null);
                                    }}
                                >
                                    Start Your Project
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="cta-section">
                <h3>Ready to Start Your Project?</h3>
                <p>Contact us to discuss your interior design needs and get a free consultation.</p>
                <button
                    className="profile-btn"
                    // style={{ margin: "20px 20px" !important" }}
                    onClick={() => setShowQuoteForm(true)}
                >
                    Get a Free Quote
                </button>
            </div>

            {showQuoteForm && (
                <ConsultationForm
                    isOpen={showQuoteForm}
                    onClose={() => setShowQuoteForm(false)}
                    type="quote"
                />
            )}
        </div>
    );
};

export default Portfolio;
